/* eslint-disable no-nested-ternary */
import React, { ReactElement, useEffect, useState } from 'react';

import format from 'date-fns/format';
import { parseISO, isAfter, isBefore } from 'date-fns';
import { Container, TableContainer } from './styles';

import api from '../../services/api';
import moment from 'moment';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import {
  filaStatusPriority,
  getFilaTextColorByStatus,
} from '../../utils/filaStatusUtil';
import { getProductNameByInternalCode } from '../../utils/getProductConfig';

interface Fila {
  filaid: number;
  produto: number;
  data: Date;
  placa: string;
  placa2: string;
  cpf: string;
  motorista: string;
  volume: number;
  status: string;
  spot: number;
  agendamentoid: number;
}

function FilaOleoFusel(): ReactElement {
  const [filaOleoFusel, setFilaOleoFusel] = useState<Fila[]>([]);

  const filaDate = new Date(Date.now());
  const formattedDate = moment.utc(Date.now()).format('YYYY-MM-DD');

  const horaInicioOleoFusel = parseISO(`${formattedDate} 07:15:00`);
  const horaInicioOleoFuselFormatted = parseISO(
    format(horaInicioOleoFusel, 'yyyy-MM-dd HH:mm:ss'),
  );
  const horaFimOleoFusel = parseISO(`${formattedDate} 14:30:00`);
  const horaFimOleoFuselFormatted = parseISO(
    format(horaFimOleoFusel, 'yyyy-MM-dd HH:mm:ss'),
  );
  const horaAtual = parseISO(format(filaDate, 'yyyy-MM-dd HH:mm:ss'));

  const group = 'Outros';

  const [timeLeft, setTimeLeft] = useState(25);

  useEffect(() => {
    async function loadFila(): Promise<void> {
      await api.get(`/fila/byprodutogroup/${group}`).then(response => {
        setFilaOleoFusel(response.data);
      });
    }
    loadFila();
  }, [formattedDate]);

  const oleoFuselCarregando = filaOleoFusel.filter(
    veiculosInFila =>
      veiculosInFila.status === 'PCAR' || veiculosInFila.status === 'ELIB',
  );

  // Verifica quantos veiculos estão no carregamento de Oleo Fusel
  if (
    isAfter(horaAtual, horaInicioOleoFuselFormatted) === true &&
    isBefore(horaAtual, horaFimOleoFuselFormatted) === true &&
    oleoFuselCarregando.length < 5
  ) {
    const minPosition = Math.min(
      ...filaOleoFusel.map(filaItem => filaItem.spot),
    );
    const [getNextInLineOleoFusel] = filaOleoFusel.filter(
      nextInLine =>
        nextInLine.status === 'AENT' && nextInLine.spot >= minPosition,
    );

    if (getNextInLineOleoFusel) {
      api.post('fila/status', {
        data: getNextInLineOleoFusel.data,
        agendamentoid: getNextInLineOleoFusel.agendamentoid,
        placa: getNextInLineOleoFusel.placa,
        statusnew: 'ELIB',
        motivo: 'Liberação de entrada Oleo Fúsel, pelo número de vagas < 5.',
      });
    }
  }

  const sortedFilaOleoFusel = filaOleoFusel
    .filter(produto => produto.produto === 8)
    .sort((a, b) => {
      const formatDateA = moment(a.data).format('YYYY-MM-DD');
      const formatDateB = moment(b.data).format('YYYY-MM-DD');
      // First, compare the dates
      if (formatDateA > formatDateB) {
        return 1;
      } else if (formatDateA < formatDateB) {
        return -1;
      } else {
        // If dates are the same, compare the filaid
        if (a.filaid > b.filaid) {
          return 1;
        } else if (a.filaid < b.filaid) {
          return -1;
        } else {
          return 1;
        }
      }
    });

  useEffect(() => {
    // Set timeout based on the length of sortedFilaOleoFusel
    let timeout: NodeJS.Timeout;

    if (sortedFilaOleoFusel === undefined || sortedFilaOleoFusel.length === 0) {
      setTimeLeft(5); // Shorter timeout if there are no items in the queue
      timeout = setTimeout(() => {
        window.location.replace('/fila');
      }, 5000); // 5 seconds timeout
    } else {
      setTimeLeft(25); // Default timeout if there are items
      timeout = setTimeout(() => {
        window.location.replace('/fila');
      }, 25000); // 25 seconds timeout
    }

    // Cleanup the timeout when the component unmounts or filaOutros changes
    return () => clearTimeout(timeout);
  }, [sortedFilaOleoFusel]);

  const sortedFilaOleoFuselStatus = sortedFilaOleoFusel.sort((a, b) => {
    return filaStatusPriority[a.status] - filaStatusPriority[b.status];
  });

  return (
    <Container>
      <header>
        <h1>Fila Carregamento - Oleo Fúsel</h1>
      </header>

      <div className="autoplay-progress" slot="container-end">
        <CountdownCircleTimer
          isPlaying
          duration={timeLeft}
          colors={['#00a859', '#F7B801', '#A30000', '#A30000']}
          colorsTime={[timeLeft, 5, 2, 0]}
          size={48}
          strokeWidth={5}
          trailStrokeWidth={8}
        >
          {({ remainingTime }) => remainingTime}
        </CountdownCircleTimer>
      </div>

      {/* Table */}
      <TableContainer>
        <table id="tableDataRegistries">
          {/* This header are being used for displaying header on the report */}
          <thead className="headTable">
            <tr>
              <th>Fila Id</th>
              <th>Agend/Certif</th>
              <th>Produto</th>
              <th>Data</th>
              <th>Placa Carreta</th>
              <th>Placa Carreta 2</th>
              <th>Nome Motorista</th>
              <th>Volume Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {sortedFilaOleoFuselStatus.map(filaData => (
              <tr
                key={filaData.filaid}
                style={{
                  color: getFilaTextColorByStatus(filaData.status).color,
                }}
              >
                <td>{filaData.filaid}</td>
                <td>{filaData.agendamentoid}</td>
                <td>{getProductNameByInternalCode(filaData.produto)}</td>
                <td>{moment.utc(filaData.data).format('DD/MM/YYYY')}</td>
                <td>{filaData.placa}</td>
                <td>{filaData.placa2}</td>
                <td>{filaData.motorista}</td>
                <td>{filaData.volume}</td>
                <td>
                  {filaData.status === 'AGPE'
                    ? 'Aguardando Pedido'
                    : filaData.status === 'APPE'
                    ? 'Aguardando Pag. Pedido'
                    : filaData.status === 'ACKL'
                    ? 'Aguardando Checklist'
                    : filaData.status === 'AENT'
                    ? 'Aguardando Liberação Entrada'
                    : filaData.status === 'ELIB'
                    ? 'Aguardando Liberação Entrada' //'Pesagem Liberada' //ELIB e AENT terao a mesma desc na fila
                    : filaData.status === 'PCAR'
                    ? 'Processo Carregamento'
                    : filaData.status === 'AFAT'
                    ? 'Aguard. Faturamento'
                    : filaData.status === 'ADOC'
                    ? 'Aguard. Docs Transportadora'
                    : filaData.status === 'NLIB'
                    ? 'Nota Fiscal Liberada Coleta'
                    : filaData.status === 'CAFI'
                    ? 'Carregamento Finalizado'
                    : 'Erro'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableContainer>
    </Container>
  );
}

export default FilaOleoFusel;
